import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter, Subject, Subscription, takeUntil } from "rxjs";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { Navigation } from "app/core/navigation/navigation.types";
import { NavigationService } from "app/core/navigation/navigation.service";
import { DrawerService } from "app/shared/service/drawer-service.service";
import { InventarioContextService } from "app/shared/service/inventario-context.service";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';

@Component({
  selector: "enterprise-layout",
  templateUrl: "./enterprise.component.html",
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('drawerOverlay', [
      state(
        'closed',
        style({
          opacity: 0,
          visibility: 'hidden',
        })
      ),
      state(
        'open',
        style({
          opacity: 1,
          visibility: 'visible',
        })
      ),
      transition('closed => open', [
        animate('250ms ease-out'),
      ]),
      transition('open => closed', [
        animate('200ms ease-in'),
      ]),
    ]),

    trigger('drawerPanel', [
      state(
        'closed',
        style({
          transform: 'translateX(100%)',
        })
      ),
      state(
        'open',
        style({
          transform: 'translateX(0)',
        })
      ),
      transition('closed => open', [
        animate('300ms cubic-bezier(0.22, 1, 0.36, 1)'),
      ]),
      transition('open => closed', [
        animate('250ms cubic-bezier(0.4, 0, 1, 1)'),
      ]),
    ]),
  ],
})
export class EnterpriseLayoutComponent implements OnInit, OnDestroy {
  exibirNavegacaoHorizontal = true;
  isScreenSmall: boolean;
  navigation: Navigation;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  idInventario!: string;
  isDrawerMenuVisible = false;
  isNavegacaoHabilitada = false;
  isMapaRoute = false;
  longitude: number | null = null;
  latitude: number | null = null;

  /** Último id válido visto na sessão (ex.: ao ir para /mapa a rota perde o :id). */
  private _ultimoIdInventarioValido = "";
  private sub!: Subscription;

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private drawerService: DrawerService,
    private _inventarioContext: InventarioContextService
  ) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._atualizarLayoutPorRota();

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => this._atualizarLayoutPorRota());

    // Mantém "Navegação do inventário" visível no mapa quando o id vem do contexto.
    this._inventarioContext.context$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this._atualizarNavegacaoHabilitada());

    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");
      });

    // Sincroniza com [opened]; drawer.toggle() deixava isDrawerMenuVisible desatualizado.
    this.sub = this.drawerService.toggle$.subscribe(() => {
      this.isDrawerMenuVisible = !this.isDrawerMenuVisible;
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this.sub.unsubscribe();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  closeDrawerMenu() {
    this.isDrawerMenuVisible = false;
  }


  abrirMenu(): void {
    this.drawerService.toggle();
  }

  private _atualizarLayoutPorRota(): void {
    const path = this._router.url.split("?")[0];
    this.isMapaRoute = path === "/mapa" || path.startsWith("/mapa/");
    this._atualizarNavegacaoHabilitada();
  }

  private _atualizarNavegacaoHabilitada(): void {
    let route = this._activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const routeId =
      route.snapshot.paramMap.get("id") ??
      route.snapshot.paramMap.get("idInventario");
    const contextId = this._inventarioContext.getIdInventario();

    const id =
      this._primeiroIdValido(
        routeId,
        contextId,
        this._ultimoIdInventarioValido
      ) ?? "";

    if (id) {
      this._ultimoIdInventarioValido = id;
      // Garante id no contexto: vários forms só gravam lat/lon.
      if (contextId !== id) {
        this._inventarioContext.setIdInventario(id);
      }
    }

    this.idInventario = id;
    this.isNavegacaoHabilitada = !!id && id !== "novo";
  }

  private _primeiroIdValido(
    ...ids: Array<string | null | undefined>
  ): string | null {
    for (const id of ids) {
      if (id && id !== "novo") {
        return id;
      }
    }
    return null;
  }
}